class ThemeDialog extends HTMLElement {
  static instanceCount = 0;
  static closeDurationMs = 300;

  constructor() {
    super();
    this.dialog = null;
    this.triggers = [];
    this.lastTrigger = null;
    this.openedByKeyboard = false;
    this.listenerController = null;
    this.isInitialized = false;
    this.isClosing = false;
    this.closeTimer = null;

    this.onDialogClose = this.onDialogClose.bind(this);
    this.onDialogCancel = this.onDialogCancel.bind(this);
    this.onDialogClick = this.onDialogClick.bind(this);
    this.onInternalClick = this.onInternalClick.bind(this);
  }

  connectedCallback() {
    if (this.isInitialized) {
      return;
    }

    this.dialog = this.querySelector('dialog');
    if (!this.dialog) {
      return;
    }

    if (!this.dialog.id) {
      ThemeDialog.instanceCount += 1;
      this.dialog.id = 'theme-dialog-' + ThemeDialog.instanceCount;
    }

    this.listenerController = new AbortController();
    this.triggers = Array.from(document.querySelectorAll('[data-toggle="' + this.dialog.id + '"]'));
    this.triggers.forEach((trigger) => {
      const onTriggerClick = this.onTriggerClick.bind(this, trigger);

      trigger.setAttribute('aria-controls', this.dialog.id);
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.addEventListener('click', onTriggerClick, { signal: this.listenerController.signal });
    });

    this.dialog.addEventListener('close', this.onDialogClose, { signal: this.listenerController.signal });
    this.dialog.addEventListener('cancel', this.onDialogCancel, { signal: this.listenerController.signal });
    this.dialog.addEventListener('click', this.onDialogClick, { signal: this.listenerController.signal });
    this.addEventListener('click', this.onInternalClick, { signal: this.listenerController.signal });
    this.openFromUrlIfNeeded();
    this.isInitialized = true;
  }

  disconnectedCallback() {
    this.unlockPageScroll();

    if (this.listenerController) {
      this.listenerController.abort();
      this.listenerController = null;
    }

    this.dialog = null;
    this.triggers = [];
    this.lastTrigger = null;
    this.openedByKeyboard = false;
    this.isInitialized = false;
    this.isClosing = false;

    if (this.closeTimer) {
      window.clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  setExpandedState(isExpanded) {
    this.triggers.forEach((trigger) => {
      trigger.setAttribute('aria-expanded', String(isExpanded));
    });
  }

  onTriggerClick(trigger, event) {
    event.preventDefault();
    this.lastTrigger = trigger;
    this.openedByKeyboard = event.detail === 0;
    this.openDialog();
  }

  openDialog() {
    if (this.dialog.open) {
      this.requestClose();
      return;
    }
    this.closeOtherOpenDialogs();
    this.dialog.classList.remove('is-closing');
    this.isClosing = false;
    this.prepareOpenAnimations();

    try {
      this.dialog.showModal();
    } catch (error) {
      console.error('Error opening dialog:', error);
      if (!this.dialog.open && this.dialog.isConnected) {
        this.dialog.setAttribute('open', '');
      }
    }

    this.dialog.dataset.openedBy = this.openedByKeyboard ? 'keyboard' : 'pointer';
    this.setExpandedState(this.dialog.open);

    if (this.dialog.open) {
      this.lockPageScroll();
      this.revealOpenAnimations();
    }
  }

  shouldAnimateOnOpen() {
    if (!this.dialog || !this.dialog.hasAttribute('data-animate-on-scroll')) {
      return false;
    }

    if (this.dialog.dataset.animateOnScroll !== 'true') {
      return false;
    }

    if (document.documentElement.dataset.designMode === 'true') {
      return false;
    }

    return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  prepareOpenAnimations() {
    if (!this.dialog || !this.dialog.hasAttribute('data-animate-on-scroll')) {
      return;
    }

    if (!this.shouldAnimateOnOpen()) {
      this.dialog.dataset.viewportEntered = 'true';
      return;
    }

    this.dialog.dataset.viewportEntered = 'false';
  }

  revealOpenAnimations() {
    if (!this.dialog || !this.dialog.hasAttribute('data-animate-on-scroll')) {
      return;
    }

    if (!this.shouldAnimateOnOpen()) {
      this.dialog.dataset.viewportEntered = 'true';
      return;
    }

    this.dialog.getBoundingClientRect();

    window.requestAnimationFrame(() => {
      this.dialog.dataset.viewportEntered = 'true';
    });
  }

  openFromUrlIfNeeded() {
    if (!this.dialog.id) {
      return;
    }

    const dialogName = this.dialog.id.endsWith('-dialog')
      ? this.dialog.id.slice(0, -'-dialog'.length)
      : this.dialog.id;
    const params = new URLSearchParams(window.location.search);
    const hash = window.location.hash;
    const shouldOpenFromQuery = params.get(dialogName) === 'open';
    const shouldOpenFromHash = hash === `#${this.dialog.id}` || hash === `#${dialogName}`;

    if (!shouldOpenFromQuery && !shouldOpenFromHash) {
      return;
    }

    window.setTimeout(() => {
      this.openDialog();
    }, 250);
  }

  onDialogClose() {
    this.dialog.classList.remove('is-closing');
    delete this.dialog.dataset.openedBy;
    if (this.dialog.hasAttribute('data-animate-on-scroll')) {
      this.dialog.dataset.viewportEntered = 'false';
    }
    this.setExpandedState(false);
    this.isClosing = false;
    this.unlockPageScroll();

    if (this.closeTimer) {
      window.clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }

    if (this.lastTrigger) {
      this.lastTrigger.focus();
    }
  }

  onDialogCancel(event) {
    event.preventDefault();
    this.requestClose();
  }

  onDialogClick(event) {
    if (event.target !== this.dialog) {
      return;
    }

    const rect = this.dialog.getBoundingClientRect();
    const clickedOutside = event.clientX < rect.left ||
      event.clientX > rect.right ||
      event.clientY < rect.top ||
      event.clientY > rect.bottom;

    if (clickedOutside) {
      this.requestClose();
    }
  }

  onInternalClick(event) {
    const closeButton = event.target.closest('[data-dialog-close]');

    if (!closeButton || !this.contains(closeButton) || !this.dialog.open) {
      return;
    }

    this.requestClose();
  }

  requestClose() {
    if (!this.dialog || !this.dialog.open || this.isClosing) {
      return;
    }

    const closeDurationMs = this.getCloseDurationMs();

    if (closeDurationMs === 0) {
      this.dialog.classList.remove('is-closing');
      this.dialog.close();
      return;
    }

    this.isClosing = true;
    this.dialog.classList.add('is-closing');

    if (this.closeTimer) {
      window.clearTimeout(this.closeTimer);
    }

    this.closeTimer = window.setTimeout(() => {
      this.closeTimer = null;

      if (this.dialog.open) {
        this.dialog.close();
      }
    }, closeDurationMs);
  }

  getCloseDurationMs() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return 0;
    }

    const computedStyle = window.getComputedStyle(this.dialog);
    const cssDuration = computedStyle.getPropertyValue('--dialog-transition-duration').trim();
    const parsedDuration = Number.parseFloat(cssDuration);

    if (!Number.isFinite(parsedDuration) || parsedDuration < 0) {
      return ThemeDialog.closeDurationMs;
    }

    if (cssDuration.endsWith('ms')) {
      return parsedDuration;
    }

    return parsedDuration * 1000;
  }

  closeOtherOpenDialogs() {
    document.querySelectorAll('dialog[open]').forEach((openDialog) => {
      let dialogHost = null;

      if (openDialog === this.dialog) {
        return;
      }

      let node = openDialog.parentElement;
      while (node) {
        if (typeof node.requestClose === 'function') {
          dialogHost = node;
          break;
        }

        node = node.parentElement;
      }

      if (dialogHost && typeof dialogHost.requestClose === 'function') {
        dialogHost.requestClose();
        return;
      }

      openDialog.close();
    });
  }

  lockPageScroll() {
    if (document.body.dataset.scrollLocked === 'true') {
      return;
    }

    const scrollY = window.scrollY;

    document.body.dataset.scrollLocked = 'true';
    document.body.dataset.scrollLockTop = String(scrollY);
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
  }

  unlockPageScroll() {
    if (document.body.dataset.scrollLocked !== 'true') {
      return;
    }

    const scrollY = Number(document.body.dataset.scrollLockTop || 0);

    delete document.body.dataset.scrollLocked;
    delete document.body.dataset.scrollLockTop;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    window.scrollTo(0, scrollY);
  }
}
customElements.define('theme-dialog', ThemeDialog);

class ThemeCollapse extends HTMLElement {
  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
    this.reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  }

  connectedCallback() {
    this.details = this.querySelector('.theme-collapse-details');
    this.summary = this.querySelector('.theme-collapse-summary');

    if (!this.details || !this.summary) {
      return;
    }

    this.summary.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    if (this.summary) {
      this.summary.removeEventListener('click', this.handleClick);
    }
  }

  handleClick(event) {
    event.preventDefault();

    if (!this.details || this.details.dataset.animating === 'true') {
      return;
    }

    this.animate();
  }

  cleanup() {
    this.details.style.height = '';
    this.details.style.overflow = '';
    this.details.dataset.animating = 'false';
  }

  animate() {
    const isOpen = this.details.hasAttribute('open');
    const startHeight = this.details.offsetHeight;
    let endHeight = this.summary.offsetHeight;

    if (this.reduceMotionQuery.matches) {
      this.details.toggleAttribute('open', !isOpen);
      return;
    }

    if (!isOpen) {
      this.details.style.height = startHeight + 'px';
      this.details.setAttribute('open', '');
      endHeight = this.details.scrollHeight;
    }

    this.details.dataset.animating = 'true';
    this.details.style.overflow = 'hidden';
    this.details.style.height = startHeight + 'px';

    requestAnimationFrame(() => {
      this.details.style.height = endHeight + 'px';
    });

    const fallbackTimer = window.setTimeout(() => {
      if (isOpen) {
        this.details.removeAttribute('open');
      }
      this.cleanup();
    }, 300);

    const handleTransitionEnd = (event) => {
      if (event.target !== this.details || event.propertyName !== 'height') {
        return;
      }

      window.clearTimeout(fallbackTimer);
      this.details.removeEventListener('transitionend', handleTransitionEnd);

      if (isOpen) {
        this.details.removeAttribute('open');
      }
      this.cleanup();
    };

    this.details.addEventListener('transitionend', handleTransitionEnd);
  }
}

if (!customElements.get('theme-collapse')) {
  customElements.define('theme-collapse', ThemeCollapse);
}

/*
  Tooltip ​‍‍‌‍‍‌‌‍​‌‍⁠​‌​‌‍​‌‌‌‍​​​‌‍​​‌​‍​​‌‌‍​‌​‌‍​‌​​‍‌​​​‍​‌‌​‍‌​​‌‍​​‌‌‍​​‌‌‍​​​​﻿‌​‌‍‍​
*/
class ThemeTooltip {
  static tooltipId = 'theme-tooltip';
  static destroyDelayMs = 150;
  static selector = '[data-tooltip]';
  static margin = 8;
  static activeTrigger = null;
  static tooltipElement = null;
  static isBound = false;
  static destroyTimer = null;

  static init() {
    if (ThemeTooltip.isBound) {
      return;
    }

    document.addEventListener('mouseover', ThemeTooltip.onMouseOver);
    document.addEventListener('mouseout', ThemeTooltip.onMouseOut);
    document.addEventListener('focusin', ThemeTooltip.onFocusIn);
    document.addEventListener('focusout', ThemeTooltip.onFocusOut);
    document.addEventListener('keydown', ThemeTooltip.onKeyDown);
    window.addEventListener('scroll', ThemeTooltip.onViewportChange, true);
    window.addEventListener('resize', ThemeTooltip.onViewportChange);
    ThemeTooltip.isBound = true;
  }

  static getTriggerFromEvent(event) {
    if (!(event.target instanceof Element)) {
      return null;
    }

    return event.target.closest(ThemeTooltip.selector);
  }

  static clearDestroyTimer() {
    if (!ThemeTooltip.destroyTimer) {
      return;
    }

    window.clearTimeout(ThemeTooltip.destroyTimer);
    ThemeTooltip.destroyTimer = null;
  }

  static clearTriggerDescription(trigger) {
    if (!trigger) {
      return;
    }

    if (trigger.getAttribute('aria-describedby') === ThemeTooltip.tooltipId) {
      trigger.removeAttribute('aria-describedby');
    }
  }

  static ensureTooltipElement() {
    const parent = document.body || document.documentElement;

    ThemeTooltip.clearDestroyTimer();

    if (ThemeTooltip.tooltipElement) {
      return;
    }

    ThemeTooltip.tooltipElement = document.createElement('div');
    ThemeTooltip.tooltipElement.id = ThemeTooltip.tooltipId;
    ThemeTooltip.tooltipElement.className = 'theme-tooltip';
    ThemeTooltip.tooltipElement.setAttribute('role', 'tooltip');
    ThemeTooltip.tooltipElement.setAttribute('aria-hidden', 'true');
    parent.appendChild(ThemeTooltip.tooltipElement);
  }

  static getTooltipHost(trigger) {
    const dialog = trigger.closest('dialog[open]');
    return dialog || document.body || document.documentElement;
  }

  static moveToHost(trigger) {
    const nextHost = ThemeTooltip.getTooltipHost(trigger);

    if (!ThemeTooltip.tooltipElement || !nextHost) {
      return;
    }

    if (ThemeTooltip.tooltipElement.parentElement !== nextHost) {
      nextHost.appendChild(ThemeTooltip.tooltipElement);
    }
  }

  static destroyTooltipElement() {
    if (!ThemeTooltip.tooltipElement) {
      return;
    }

    ThemeTooltip.tooltipElement.remove();
    ThemeTooltip.tooltipElement = null;
  }

  static onMouseOver(event) {
    const trigger = ThemeTooltip.getTriggerFromEvent(event);
    if (!trigger) {
      return;
    }

    ThemeTooltip.show(trigger);
  }

  static onMouseOut(event) {
    const trigger = ThemeTooltip.getTriggerFromEvent(event);
    if (!trigger || !ThemeTooltip.activeTrigger) {
      return;
    }

    if (trigger.contains(event.relatedTarget)) {
      return;
    }

    if (ThemeTooltip.activeTrigger === trigger) {
      ThemeTooltip.hide();
    }
  }

  static onFocusIn(event) {
    const trigger = ThemeTooltip.getTriggerFromEvent(event);
    if (!trigger) {
      return;
    }

    ThemeTooltip.show(trigger);
  }

  static onFocusOut(event) {
    if (!ThemeTooltip.activeTrigger) {
      return;
    }

    if (ThemeTooltip.activeTrigger.contains(event.relatedTarget)) {
      return;
    }

    ThemeTooltip.hide();
  }

  static onKeyDown(event) {
    if (event.key === 'Escape') {
      ThemeTooltip.hide();
    }
  }

  static onViewportChange() {
    if (!ThemeTooltip.activeTrigger) {
      return;
    }

    ThemeTooltip.position();
  }

  static isTouchDevice() {
    return window.matchMedia('(hover: none), (pointer: coarse)').matches;
  }

  static show(trigger) {
    if (ThemeTooltip.isTouchDevice()) {
      ThemeTooltip.hide();
      return;
    }

    const text = trigger.getAttribute('data-tooltip');

    if (!text) {
      return;
    }

    if (ThemeTooltip.activeTrigger && ThemeTooltip.activeTrigger !== trigger) {
      ThemeTooltip.clearTriggerDescription(ThemeTooltip.activeTrigger);
    }

    ThemeTooltip.ensureTooltipElement();
    ThemeTooltip.moveToHost(trigger);
    ThemeTooltip.activeTrigger = trigger;
    ThemeTooltip.tooltipElement.textContent = text;
    ThemeTooltip.tooltipElement.setAttribute('aria-hidden', 'false');
    ThemeTooltip.tooltipElement.classList.add('is-visible');
    trigger.setAttribute('aria-describedby', ThemeTooltip.tooltipId);
    ThemeTooltip.position();
  }

  static hide() {
    if (!ThemeTooltip.tooltipElement || !ThemeTooltip.activeTrigger) {
      return;
    }

    ThemeTooltip.clearTriggerDescription(ThemeTooltip.activeTrigger);

    ThemeTooltip.tooltipElement.classList.remove('is-visible');
    ThemeTooltip.tooltipElement.setAttribute('aria-hidden', 'true');
    ThemeTooltip.activeTrigger = null;

    ThemeTooltip.clearDestroyTimer();

    ThemeTooltip.destroyTimer = window.setTimeout(() => {
      ThemeTooltip.destroyTimer = null;
      ThemeTooltip.destroyTooltipElement();
    }, ThemeTooltip.destroyDelayMs);
  }

  static position() {
    if (!ThemeTooltip.tooltipElement || !ThemeTooltip.activeTrigger) {
      return;
    }

    const rect = ThemeTooltip.activeTrigger.getBoundingClientRect();
    const tooltipRect = ThemeTooltip.tooltipElement.getBoundingClientRect();
    const hostRect = ThemeTooltip.tooltipElement.parentElement instanceof HTMLDialogElement
      ? ThemeTooltip.tooltipElement.parentElement.getBoundingClientRect()
      : { left: 0, top: 0 };

    let left = rect.left - hostRect.left + ((rect.width - tooltipRect.width) / 2);
    const maxLeft = window.innerWidth - hostRect.left - tooltipRect.width - ThemeTooltip.margin;
    left = Math.min(Math.max(ThemeTooltip.margin, left), Math.max(ThemeTooltip.margin, maxLeft));
    let top = rect.top - hostRect.top - tooltipRect.height - ThemeTooltip.margin;

    if (top < ThemeTooltip.margin) {
      top = rect.bottom - hostRect.top + ThemeTooltip.margin;
    }

    ThemeTooltip.tooltipElement.style.left = left + 'px';
    ThemeTooltip.tooltipElement.style.top = top + 'px';
  }
}
ThemeTooltip.init();

class ThemeCarousel extends HTMLElement {
  constructor() {
    super();
    this.reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.focusableUpdateFrame = null;
    this.progressUpdateFrame = null;
    this.layoutUpdateFrame = null;
    this.autoplayTimer = null;
    this.touchStartX = null;
    this.resizeObserver = null;
    this.imageLoadHandlers = new Map();
    this.alignmentImagesSettled = new WeakSet();
    this.boundHandlers = {
      prev: () => this.prevSlide(),
      next: () => this.nextSlide(),
      resize: () => this.scheduleLayoutUpdate(),
      startAutoplay: () => this.startAutoplay(),
      stopAutoplay: () => this.stopAutoplay(),
      trackScroll: () => {
        this.updateFocusableItemsInView();
        this.scheduleProgressUpdate();
      },
      trackKeydown: (event) => this.onTrackKeydown(event),
      trackTouchStart: (event) => {
        this.stopAutoplay();
        this.handleFirstLastScrollItems(event, 'start');
      },
      trackTouchEnd: (event) => {
        this.handleFirstLastScrollItems(event, 'end');
        this.startAutoplay();
      }
    };
  }

  connectedCallback() {
    this.initCarousel();
  }

  disconnectedCallback() {
    if (this.prevBtn) {
      this.prevBtn.removeEventListener('click', this.boundHandlers.prev);
    }
    if (this.nextBtn) {
      this.nextBtn.removeEventListener('click', this.boundHandlers.next);
    }
    if (this.track) {
      this.track.removeEventListener('keydown', this.boundHandlers.trackKeydown);
      this.track.removeEventListener('scroll', this.boundHandlers.trackScroll);
      this.track.removeEventListener('touchstart', this.boundHandlers.trackTouchStart);
      this.track.removeEventListener('touchend', this.boundHandlers.trackTouchEnd);
    }
    this.removeEventListener('mouseenter', this.boundHandlers.stopAutoplay);
    this.removeEventListener('mouseleave', this.boundHandlers.startAutoplay);
    this.removeEventListener('focusin', this.boundHandlers.stopAutoplay);
    this.removeEventListener('focusout', this.boundHandlers.startAutoplay);
    window.removeEventListener('resize', this.boundHandlers.resize);
    this.stopAutoplay();
    if (this.focusableUpdateFrame) {
      window.cancelAnimationFrame(this.focusableUpdateFrame);
      this.focusableUpdateFrame = null;
    }
    if (this.progressUpdateFrame) {
      window.cancelAnimationFrame(this.progressUpdateFrame);
      this.progressUpdateFrame = null;
    }
    if (this.layoutUpdateFrame) {
      window.cancelAnimationFrame(this.layoutUpdateFrame);
      this.layoutUpdateFrame = null;
    }
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.imageLoadHandlers.forEach((handler, image) => {
      image.removeEventListener('load', handler);
      image.removeEventListener('error', handler);
    });
    this.imageLoadHandlers.clear();
  }

  initCarousel() {
    this.track = this.querySelector('.theme-carousel-track');
    if (!this.track) {
      return;
    }

    this.items = this.track ? this.track.querySelectorAll('.theme-carousel-item') : this.querySelectorAll('.theme-carousel-item');
    this.prevBtn = this.querySelector('[data-prev]');
    this.nextBtn = this.querySelector('[data-next]');
    this.progressBar = this.querySelector('.theme-carousel-progress-bar');
    this.counterCurrent = this.querySelector('.theme-carousel-counter-current');
    this.counterTotal = this.querySelector('.theme-carousel-counter-total');

    this.setupAccessibility();

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', this.boundHandlers.prev);
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', this.boundHandlers.next);
    }

    this.scheduleLayoutUpdate();
    window.addEventListener('resize', this.boundHandlers.resize);
    this.setupAutoplay();

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.boundHandlers.resize);
      this.resizeObserver.observe(this);
      this.resizeObserver.observe(this.track);
    }
  }

  setupAccessibility() {
    this.track.addEventListener('keydown', this.boundHandlers.trackKeydown);
    this.track.addEventListener('scroll', this.boundHandlers.trackScroll, { passive: true });
    this.track.addEventListener('touchstart', this.boundHandlers.trackTouchStart, { passive: true });
    this.track.addEventListener('touchend', this.boundHandlers.trackTouchEnd, { passive: true });
    this.updateFocusableItemsInView();
  }

  setupAutoplay() {
    if (this.dataset.autoplay !== 'true') {
      return;
    }

    this.addEventListener('mouseenter', this.boundHandlers.stopAutoplay);
    this.addEventListener('mouseleave', this.boundHandlers.startAutoplay);
    this.addEventListener('focusin', this.boundHandlers.stopAutoplay);
    this.addEventListener('focusout', this.boundHandlers.startAutoplay);
    this.startAutoplay();
  }

  getAutoplayInterval() {
    const interval = Number(this.dataset.autoplayInterval || 5);
    return Math.max(interval, 1) * 1000;
  }

  startAutoplay() {
    if (this.dataset.autoplay !== 'true' || this.reduceMotionQuery.matches || this.autoplayTimer) {
      return;
    }

    if (!this.track || !this.items.length || this.dataset.scrollable === 'false') {
      return;
    }

    this.autoplayTimer = window.setInterval(() => {
      if (!document.contains(this) || this.dataset.scrollable === 'false') {
        this.stopAutoplay();
        return;
      }

      this.nextSlide();
    }, this.getAutoplayInterval());
  }

  stopAutoplay() {
    if (!this.autoplayTimer) {
      return;
    }

    window.clearInterval(this.autoplayTimer);
    this.autoplayTimer = null;
  }

  handleFirstLastScrollItems(event, phase) {
    const swipeThreshold = 24;
    const touchStart = event.touches && event.touches[0];
    const touchEnd = event.changedTouches && event.changedTouches[0];

    if (phase === 'start') {
      if (!touchStart) {
        return;
      }

      this.touchStartX = touchStart.clientX;
      return;
    }

    if (this.touchStartX === null || !touchEnd) {
      this.touchStartX = null;
      return;
    }

    const deltaX = this.touchStartX - touchEnd.clientX;
    const swipedLeft = deltaX > swipeThreshold;
    const swipedRight = deltaX < -swipeThreshold;

    if (swipedLeft && this.isAtEnd()) {
      this.track.scrollTo({ left: 0, behavior: 'auto' });
    }

    if (swipedRight && this.isAtStart()) {
      this.track.scrollTo({ left: this.getMaxScroll(), behavior: 'auto' });
    }

    this.touchStartX = null;
  }

  getVisibleItemRange() {
    const trackRect = this.track.getBoundingClientRect();
    let firstVisibleIndex = -1;
    let lastVisibleIndex = -1;

    this.items.forEach((item, index) => {
      const itemRect = item.getBoundingClientRect();
      const visibleWidth = Math.min(itemRect.right, trackRect.right) - Math.max(itemRect.left, trackRect.left);
      const isInView = visibleWidth > 0 && itemRect.width > 0 && visibleWidth / itemRect.width >= 0.5;

      if (isInView) {
        if (firstVisibleIndex === -1) {
          firstVisibleIndex = index;
        }
        lastVisibleIndex = index;
      }
    });

    return { firstVisibleIndex, lastVisibleIndex };
  }

  updateProgress() {
    if (!this.track || !this.items.length) {
      return;
    }

    if (this.track.scrollWidth <= 0) {
      return;
    }

    const maxScroll = Math.max(this.getMaxScroll(), 0);
    const isScrollable = maxScroll > 1;
    const scrollLeft = Math.min(Math.max(this.track.scrollLeft, 0), maxScroll);

    this.dataset.scrollable = isScrollable ? 'true' : 'false';
    if (!isScrollable) {
      this.stopAutoplay();
    }

    if (this.progressBar) {
      const progressWidth = (this.track.clientWidth / this.track.scrollWidth) * 100;
      const progressTrackWidth = this.progressBar.parentElement?.clientWidth || 0;
      const progressOffset = progressTrackWidth * (scrollLeft / this.track.scrollWidth);

      this.progressBar.style.width = `${progressWidth}%`;
      this.progressBar.style.transform = `translateX(${progressOffset}px)`;
    }

    if (this.counterCurrent && this.counterTotal) {
      const { lastVisibleIndex } = this.getVisibleItemRange();
      const current = lastVisibleIndex >= 0 ? lastVisibleIndex + 1 : 1;

      this.counterCurrent.textContent = current.toString();
      this.counterTotal.textContent = this.items.length.toString();
    }
  }

  scheduleProgressUpdate() {
    if (this.progressUpdateFrame) {
      return;
    }

    this.progressUpdateFrame = window.requestAnimationFrame(() => {
      this.progressUpdateFrame = null;
      this.updateProgress();
    });
  }

  scheduleLayoutUpdate() {
    if (this.layoutUpdateFrame) {
      return;
    }

    this.layoutUpdateFrame = window.requestAnimationFrame(() => {
      this.layoutUpdateFrame = null;
      this.refreshItems();
      this.positionButtons();
      this.updateFocusableItemsInView();
      this.updateProgress();
      this.startAutoplay();
    });
  }

  refreshItems() {
    this.items = this.track ? this.track.querySelectorAll('.theme-carousel-item') : this.querySelectorAll('.theme-carousel-item');
  }

  updateFocusableItemsInView() {
    if (!this.track || !this.items.length || this.focusableUpdateFrame) {
      return;
    }

    this.focusableUpdateFrame = window.requestAnimationFrame(() => {
      this.focusableUpdateFrame = null;
      const trackRect = this.track.getBoundingClientRect();

      this.items.forEach((item) => {
        const itemRect = item.getBoundingClientRect();
        const visibleWidth = Math.min(itemRect.right, trackRect.right) - Math.max(itemRect.left, trackRect.left);
        const isInView = visibleWidth > 0 && itemRect.width > 0 && visibleWidth / itemRect.width >= 0.6;
        const focusables = item.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]'
        );

        focusables.forEach((element) => {
          if (isInView) {
            element.removeAttribute('tabindex');
          } else {
            element.setAttribute('tabindex', '-1');
          }
        });
      });
    });
  }

  positionButtons() {
    if (!this.track || !this.prevBtn || !this.nextBtn) {
      return;
    }

    requestAnimationFrame(() => {
      this.refreshItems();
      let alignmentElement = this.track;

      if (this.dataset.fixArrowsForImage !== 'true') {
        this.setButtonsTopFromElement(alignmentElement);
        return; 
      }

      const firstItem = this.items[0];
      const img = firstItem?.querySelector('img');

      if (img) {
        alignmentElement = img.closest('.product-card-media, .collection-card-media, .blog-card-image, .image-block') || img;
      }

      this.setButtonsTopFromElement(alignmentElement);

      if (img) {
        this.watchAlignmentImage(img);
      }
    });
  }

  watchAlignmentImage(img) {
    if (this.imageLoadHandlers.has(img) || this.alignmentImagesSettled.has(img)) {
      return;
    }

    const handler = () => {
      this.alignmentImagesSettled.add(img);
      this.imageLoadHandlers.delete(img);
      img.removeEventListener('load', handler);
      img.removeEventListener('error', handler);
      this.scheduleLayoutUpdate();
    };

    if (img.complete) {
      this.alignmentImagesSettled.add(img);

      if (typeof img.decode === 'function') {
        img.decode().then(() => this.scheduleLayoutUpdate()).catch(() => this.scheduleLayoutUpdate());
      }

      return;
    }

    this.imageLoadHandlers.set(img, handler);
    img.addEventListener('load', handler, { once: true });
    img.addEventListener('error', handler, { once: true });
  }

  setButtonsTopFromElement(element) {
    if (!element || !this.prevBtn || !this.nextBtn) {
      return;
    }

    const carouselRect = this.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    const buttonTop = elementRect.top - carouselRect.top + (elementRect.height / 2);

    if (buttonTop > 0) {
      this.prevBtn.style.top = `${buttonTop}px`;
      this.nextBtn.style.top = `${buttonTop}px`;
    }
  }

  getGapValue() {
    const computedStyle = getComputedStyle(this);
    const gapValue = computedStyle.getPropertyValue('--column-gap')?.trim() || '1rem';
    return parseFloat(gapValue) * (gapValue.includes('rem') ? 16 : 1);
  }

  getScrollAmount() {
    const firstItem = this.items[0];
    if (!firstItem) {
      return 0;
    }
    return firstItem.offsetWidth + this.getGapValue();
  }

  getMaxScroll() {
    return this.track.scrollWidth - this.track.clientWidth;
  }

  getScrollBehavior() {
    return this.reduceMotionQuery.matches ? 'auto' : 'smooth';
  }

  isAtStart() {
    return this.track.scrollLeft <= 10;
  }

  isAtEnd() {
    return this.track.scrollLeft >= this.getMaxScroll() - 10;
  }

  onTrackKeydown(event) {
    if (!this.track || !this.items.length) {
      return;
    }

    if (event.target instanceof HTMLInputElement && event.target.type === 'radio') {
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.nextSlide();
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.prevSlide();
    }

    if (event.key === 'Home') {
      event.preventDefault();
      this.track.scrollTo({ left: 0, behavior: this.getScrollBehavior() });
    }

    if (event.key === 'End') {
      event.preventDefault();
      this.track.scrollTo({ left: this.getMaxScroll(), behavior: this.getScrollBehavior() });
    }
  }

  nextSlide() {
    if (!this.track || !this.items.length) {
      return;
    }

    if (this.isAtEnd()) {
      this.track.scrollTo({ left: 0, behavior: this.getScrollBehavior() });
    } else {
      this.track.scrollBy({ left: this.getScrollAmount(), behavior: this.getScrollBehavior() });
    }
  }

  prevSlide() {
    if (!this.track || !this.items.length) {
      return;
    }

    if (this.isAtStart()) {
      this.track.scrollTo({ left: this.getMaxScroll(), behavior: this.getScrollBehavior() });
    } else {
      this.track.scrollBy({ left: -this.getScrollAmount(), behavior: this.getScrollBehavior() });
    }
  }
}
customElements.define('theme-carousel', ThemeCarousel);

class ThemeDropdown extends HTMLElement {
  constructor() {
    super();
    this.handleButtonClick = this.handleButtonClick.bind(this);
    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
  }

  connectedCallback() {
    this.button = this.querySelector('.theme-dropdown-btn');
    this.content = this.querySelector('.theme-dropdown-content');

    if (!this.button || !this.content) return;

    this.button.addEventListener('click', this.handleButtonClick);
    document.addEventListener('click', this.handleDocumentClick);
    this.addEventListener('keydown', this.handleKeydown);
  }

  disconnectedCallback() {
    if (this.button) {
      this.button.removeEventListener('click', this.handleButtonClick);
    }

    document.removeEventListener('click', this.handleDocumentClick);
    this.removeEventListener('keydown', this.handleKeydown);
  }

  handleButtonClick(event) {
    event.preventDefault();
    this.toggle();
  }

  handleDocumentClick(event) {
    if (this.contains(event.target)) return;

    this.close();
  }

  handleKeydown(event) {
    if (event.key !== 'Escape') return;

    this.close();
    this.button?.focus();
  }

  toggle() {
    if (this.button?.getAttribute('aria-expanded') === 'true') {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    if (!this.button || !this.content) return;

    this.button.setAttribute('aria-expanded', 'true');
    this.content.hidden = false;
  }

  close() {
    if (!this.button || !this.content) return;

    this.button.setAttribute('aria-expanded', 'false');
    this.content.hidden = true;
  }
}
customElements.define('theme-dropdown', ThemeDropdown);

class LocalizationCountryFilter extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-localization-country-filter-input]');
    this.items = Array.from(this.querySelectorAll('[data-localization-country-item]'));
    this.emptyMessage = this.querySelector('.localization-filter-empty');
    this.status = this.querySelector('[data-localization-country-filter-status]');
    this.handleInput = this.handleInput.bind(this);

    this.input?.addEventListener('input', this.handleInput);
    this.updateStatus(this.items.length);
  }

  disconnectedCallback() {
    this.input?.removeEventListener('input', this.handleInput);
  }

  handleInput() {
    const query = this.input.value.trim().toLowerCase();
    let visibleCount = 0;

    this.items.forEach((item) => {
      const text = item.dataset.filterText.toLowerCase();
      const isVisible = !query || text.includes(query);

      item.hidden = !isVisible;
      if (isVisible) {
        visibleCount += 1;
      }
    });

    if (this.emptyMessage) {
      this.emptyMessage.hidden = visibleCount > 0;
    }

    this.updateStatus(visibleCount);
  }

  updateStatus(visibleCount) {
    if (!this.status) {
      return;
    }

    if (visibleCount === 0) {
      this.status.textContent = this.status.dataset.textNoResults;
      return;
    }

    const text = visibleCount === 1
      ? this.status.dataset.textResultsOne
      : this.status.dataset.textResultsOther;

    this.status.textContent = text.replace('[count]', visibleCount);
  }
}
customElements.define('localization-country-filter', LocalizationCountryFilter);

class CountdownComponent extends HTMLElement {
  constructor() {
    super();

    const countdown = Number(this.dataset.countdown);

    if (isNaN(countdown)) {
      return;
    }

    this.myInterval = setInterval(() => {
      const now = new Date().getTime();
      const diff = countdown - now;
      this.update(diff);
    }, 1000);
  }

  update(diff) {
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    const daysElem = this.querySelector("[data-countdown-days]");
    const hoursElem = this.querySelector("[data-countdown-hours]");
    const minutesElem = this.querySelector("[data-countdown-minutes]");
    const secondsElem = this.querySelector("[data-countdown-seconds]");

    if (days > 0) {
      daysElem.textContent = days;
    } else {
      daysElem?.parentElement.remove();
    }

    if (hours > 0) {
      hoursElem.textContent = hours;
    } else {
      hoursElem?.parentElement.remove();
    }

    minutesElem.textContent = minutes;
    secondsElem.textContent = seconds;
    
    if (diff < 0) {
      clearInterval(this.myInterval);
      this.closest('[data-has-countdown]')?.setAttribute("data-has-countdown", "false");
      this.remove();
    }
  }
}
customElements.define("countdown-component", CountdownComponent);

class ShareComponent extends HTMLElement {
  constructor() {
    super();

    this.shareButton = this.querySelector(".btn-share");
    this.themeDialog = this.querySelector("theme-dialog");

    if (navigator.share) {
      // Use native share API if available
      this.shareButton.addEventListener("click", (e) => {
        e.preventDefault();
        navigator.share({
          title: this.shareButton.dataset.title,
          url: window.location.href,
        });
      });
      // Remove dialog since we don't need it
      if (this.themeDialog) {
        this.themeDialog.remove();
      }
    } else {
      // Move dialog to document body for proper animations
      if (this.themeDialog) {
        document.body.appendChild(this.themeDialog);
      }

      // Set up copy button functionality
      const copyButton = document.querySelector("#share-dialog .btn-copy-link");
      if (copyButton) {
        copyButton.addEventListener("click", () => {
          const input = document.querySelector("#share-dialog .share-url-input");
          if (input) {
            navigator.clipboard.writeText(input.value).then(() => {
              copyButton.textContent = copyButton.dataset.textCopied;
              setTimeout(() => {
                copyButton.textContent = copyButton.dataset.textCopy;
              }, 2000);
            });
          }
        });
      }
    }
  }
}
customElements.define("share-component", ShareComponent);
